import { winnerContract } from '@daymade/api-contracts';
import { PrizeDisplayGroup, WinType } from '@daymade/common-lib/dbEnums';
import { ClientInferResponseBody } from '@ts-rest/core';
import CreateAccountPopup, {
  Prompt,
} from 'components/account/CreateAccountPopup';
import MainLayout from 'components/layout/MainLayout';
import PageTitle from 'components/PageTitle';
import ChoosePrizeModal from 'components/prizes/ChoosePrizeModal';
import { RedemptionFlow } from 'components/prizes/redemption-flow/RedemptionFlow';
import TestimonialAdSection from 'components/TestimonialAdSection';
import { useGuestDetails } from 'hooks/prizes/useGuestDetails';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { winnerApiClient } from 'utils/apiClient';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';

type TAvailablePrizes = ClientInferResponseBody<
  typeof winnerContract.auth.getAvailablePrizesForRedemption,
  200
>;

type TSelectedPrize = ClientInferResponseBody<
  typeof winnerContract.auth.getPrizeBySlugForRedemption,
  200
>;

interface WinTypeRecord {
  title: string;
  subtitle: string;
  type: WinType;
}

const winTypes: Record<string, WinTypeRecord> = {
  treat: {
    type: WinType.TREAT,
    title: 'Treat',
    subtitle:
      'You’ve won a Treat this week. Just click on a treat below to learn more, and click the orange button to claim it!',
  },
  prize: {
    type: WinType.PRIZE,
    title: 'Prize',
    subtitle:
      'Click on a prize to learn more — if you fancy the prize, click the orange button to claim it!',
  },
};

const RedeemPrizePage = observer(() => {
  // Stores
  const { updateCurrentUser } = useUserStore();
  const { displayToast, navigateToMyPrizes } = useUIStore();

  // Router settings
  const router = useRouter();

  // States
  const [winType, setWinType] = useState<WinTypeRecord>();

  const { prizeWinId, emailAddress } = router.query as {
    prizeWinId?: string;
    emailAddress?: string;
  };

  const [availablePrizes, setAvailablePrizes] = useState<TAvailablePrizes>();

  useEffect(() => {
    (async () => {
      if (prizeWinId) {
        const prizesResp =
          await winnerApiClient.auth.getAvailablePrizesForRedemption({
            query: { prizeWinId },
          });
        switch (prizesResp.status) {
          case HttpStatusCode.OK:
            setAvailablePrizes(prizesResp.body);

            if (prizesResp.body.winType === WinType.TREAT) {
              setWinType(winTypes['treat']);
            } else {
              setWinType(winTypes['prize']);
            }
            return;
          case HttpStatusCode.BAD_REQUEST:
            displayToast({
              title: prizesResp.body.message,
              color: '#FF6600',
              timeout: 5000,
            });
        }
      }
      router.push('/my-wins');
    })();
  }, []);

  const { guestDetails } = useGuestDetails(prizeWinId, emailAddress);

  const [selectedPrize, setSelectedPrize] = useState<TSelectedPrize | null>(
    null,
  );

  const handleSelectPrize = async (slug: string) => {
    const resp = await winnerApiClient.auth.getPrizeBySlugForRedemption({
      query: { slug },
    });

    if (resp.status === HttpStatusCode.OK) {
      setSelectedPrize(resp.body);
    }
  };

  // Only shown if guest player tries to redeem a prize.
  const [createAccountPopupAccountInfo, setCreateAccountPopupAccountInfo] =
    useState<{
      userId: string;
      emailAddress: string;
      wonByGuest: string;
    } | null>(null);

  // useEffect(() => {
  //   if (guestDetails?.wonByGuest) {
  //     setCreateAccountPopupAccountInfo(guestDetails);
  //   }
  // }, []);

  return (
    <MainLayout additionalClassName="prizesPage">
      {createAccountPopupAccountInfo && (
        <CreateAccountPopup
          prompt={Prompt.INTRO_BLOCKING_PRIZE_REDEMPTION}
          userId={createAccountPopupAccountInfo.userId}
          emailAddress={createAccountPopupAccountInfo.emailAddress}
          onClose={() => setCreateAccountPopupAccountInfo(null)}
        />
      )}

      {selectedPrize && (
        <ChoosePrizeModal
          selectedPrize={selectedPrize}
          sourceType="prizeWin"
          sourceId={prizeWinId!}
          closePopup={() => setSelectedPrize(null)}
          onSuccessfulChoiceHandler={async () => {
            updateCurrentUser();
            setSelectedPrize(null);
            navigateToMyPrizes();
          }}
          winType={winType?.type}
        />
      )}

      <PageTitle
        heroBackgroundImage={`${ASSET_PREFIX}/images/prizes/hero-background.svg`}
        subheadingComponent={
          <p style={{ fontSize: '1.1rem', textAlign: 'center' }}>
            {winType?.subtitle}
          </p>
        }
        style={{
          minHeight: 170,
        }}
      >
        Choose Your <strong>{winType?.title}</strong>
      </PageTitle>

      {availablePrizes && availablePrizes.allAvailablePrizes.length > 0 && (
        <RedemptionFlow
          prizes={{
            TRENDING_GRID:
              availablePrizes.displayGroupPrizes[PrizeDisplayGroup.TRENDING],
            HERO: availablePrizes.displayGroupPrizes[PrizeDisplayGroup.HERO],
            ALL: availablePrizes.allAvailablePrizes,
          }}
          handleSelectPrize={handleSelectPrize}
        />
      )}

      <p
        style={{
          textAlign: 'center',
          margin: '2em',
          fontStyle: 'italic',
          fontWeight: 600,
          maxWidth: '1000px',
          fontFamily: '"Montserrat", sans-serif',
        }}
      >
        Please note that products are subject to availability.
        {/* {validationResult?.prizeValue > 100 && ( // TBC: Only include following message if the prize being redeemed is worth more than £100
            <>
              <br />
              <br />
              All listed travel itineraries (including flights, hotels,
              activities, and other expenditures) are subject to change based on
              availability, price fluctuations, and unforeseen travel
              restrictions. We will work with you to create the most epic
              adventure possible!
            </>
          )} */}
      </p>

      <TestimonialAdSection />

      <style jsx>{`
        .enjoyLaterBadgeContainer {
          position: absolute;
          width: 100%;
          max-width: 1000px;
          top: 100px;
          text-align: right;
        }

        .enjoyLaterBadge {
          display: inline;
          width: 40%;
          max-width: 192px;
          height: auto;
        }

        :global(.trendingPrizesContainer) {
          width: 100%;
          display: flex;
          flex-wrap: wrap;
          grid-gap: 5px;
          padding-bottom: 0.5em;
        }

        :global(.trendingPrizesGrid) {
          width: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5px;
          padding-bottom: 0.5em;
        }

        @media (min-width: 800px) {
          :global(.trendingPrizesGrid) {
            grid-template-columns: 1fr 1fr 1fr 1fr;
          }
        }

        :global(.footer) {
          margin-top: 0 !important;
        }

        :global(.bottomInfoSection .questionHeading) {
          font-size: 1em;
          letter-spacing: normal;
          text-transform: none;
          margin: 1em 0;
        }
      `}</style>
    </MainLayout>
  );
});

export default RedeemPrizePage;
